import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Scale component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconScale extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M11.8949 13.5294C10.7064 13.5294 9.59575 13.817 8.75841 14.3194C7.93531 14.8133 7.26449 15.5951 7.26449 16.5937C7.26449 17.5922 7.93532 18.374 8.75841 18.8679C9.59574 19.3703 10.7064 19.6579 11.8949 19.6579C13.0834 19.6579 14.194 19.3703 15.0313 18.8679C15.8544 18.374 16.5252 17.5922 16.5252 16.5937C16.5252 15.5951 15.8544 14.8133 15.0313 14.3194C14.194 13.817 13.0834 13.5294 11.8949 13.5294ZM8.69446 16.5937C8.69446 16.2948 8.90013 15.902 9.49413 15.5456C10.0739 15.1977 10.921 14.9594 11.8949 14.9594C12.8688 14.9594 13.7158 15.1977 14.2956 15.5456C14.8896 15.902 15.0953 16.2948 15.0953 16.5937C15.0953 16.8926 14.8896 17.2853 14.2956 17.6417C13.7158 17.9896 12.8688 18.2279 11.8949 18.2279C10.921 18.2279 10.0739 17.9896 9.49413 17.6417C8.90013 17.2853 8.69446 16.8926 8.69446 16.5937Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M1 1.71498C1 1.32011 1.32011 1 1.71498 1H22.075C22.4699 1 22.79 1.32011 22.79 1.71498C22.79 3.46168 21.2884 4.81416 19.3929 5.68841C18.5533 6.07562 17.5856 6.39574 16.5258 6.63503V10.3969H18.7117C19.2188 10.3813 19.7167 10.5353 20.1266 10.8345C20.5403 11.1365 20.8402 11.5689 20.9782 12.0619L22.6383 17.966L22.6422 17.9807C22.7885 18.5468 22.8036 19.1387 22.6869 19.7113C22.5701 20.2839 22.3245 20.8225 21.9684 21.2862C21.6123 21.7498 21.1555 22.126 20.6323 22.3866C20.1092 22.6471 19.5333 22.7851 18.9488 22.7899L18.943 22.7899H4.84169C4.25721 22.7851 3.68154 22.6472 3.15838 22.3867C2.63529 22.1261 2.17813 21.7498 1.82207 21.2861C1.46607 20.8226 1.22036 20.284 1.10361 19.7113C0.986847 19.1386 1.00212 18.5467 1.14827 17.9808L1.15033 17.9728L2.74943 12.0626C2.8874 11.5692 3.18753 11.1366 3.60133 10.8345C4.01123 10.5353 4.50913 10.3813 5.01616 10.3969H7.26449V6.63509C6.20458 6.3958 5.23673 6.07565 4.39711 5.68841C2.50156 4.81416 1 3.46168 1 1.71498ZM15.6428 5.35817C15.6641 5.35304 15.6857 5.34887 15.7077 5.34569C16.8802 5.1181 17.9265 4.79 18.794 4.38989C20.1007 3.78724 20.8676 3.09296 21.186 2.42997H2.60396C2.92244 3.09296 3.68933 3.78724 4.996 4.38989C5.86332 4.78991 6.90936 5.11796 8.08154 5.34555C8.10422 5.34879 8.12655 5.35309 8.14848 5.35841C9.29479 5.57634 10.5598 5.69847 11.895 5.69847C13.2307 5.69847 14.4961 5.57625 15.6428 5.35817ZM15.0958 10.3969V6.89993C14.0814 7.04898 13.0058 7.12844 11.895 7.12844C10.7843 7.12844 9.70876 7.04899 8.69446 6.89996V10.3969H15.0958ZM7.99291 11.8268H15.7974L15.8108 11.827L15.8242 11.8268H18.7237C18.7325 11.8268 18.7413 11.8267 18.7501 11.8264C18.9412 11.8193 19.1291 11.8768 19.2835 11.9895C19.4378 12.1021 19.5498 12.2635 19.6013 12.4477L21.2595 18.3453C21.3497 18.6987 21.3586 19.0681 21.2857 19.4256C21.2124 19.7855 21.058 20.1239 20.8343 20.4152C20.6106 20.7064 20.3234 20.9429 19.9947 21.1067C19.6668 21.2699 19.306 21.3565 18.9399 21.36H4.85067C4.48445 21.3565 4.12375 21.2699 3.79583 21.1067C3.46697 20.9429 3.17985 20.7064 2.95621 20.4152C2.7325 20.1239 2.57811 19.7855 2.50475 19.4256C2.43165 19.0671 2.44092 18.6965 2.53186 18.342L4.12729 12.4452C4.17907 12.2622 4.29071 12.1017 4.44445 11.9895C4.59886 11.8768 4.78673 11.8193 4.97776 11.8264C4.98657 11.8267 4.99538 11.8268 5.00419 11.8268H7.96604L7.97948 11.827L7.99291 11.8268Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-scale') === undefined) {
  customElements.define('cat-icon-scale', BlocksIconScale);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-scale': BlocksIconScale;
  }
}
